import type { CSSProperties } from 'react';

import { STYLE_COMMANDS } from '../../../../../../common/richText/richTextConstants';
import {
    TEXT_BACKGROUND_PRESETS,
    TEXT_COLOR_PRESETS,
} from '../../../../../components/editor/plugins/textColor/textColorConstants';

const TEXT_STYLE_PRESET_MAP: Record<string, CSSProperties> = {
    [TEXT_COLOR_PRESETS.BLUE]: { color: 'var(--ws-text-color-blue)' },
    [TEXT_COLOR_PRESETS.GREEN]: { color: 'var(--ws-text-color-green)' },
    [TEXT_COLOR_PRESETS.ORANGE]: { color: 'var(--ws-text-color-orange)' },
    [TEXT_COLOR_PRESETS.RED]: { color: 'var(--ws-text-color-red)' },
    [TEXT_COLOR_PRESETS.GREY]: { color: 'var(--ws-text-color-grey)' },
    [TEXT_BACKGROUND_PRESETS.DARK_GREY]: {
        background: 'var(--ws-text-background-color-dark-grey)',
        color: 'var(--ws-text-background-color-dark-grey-text)',
    },
    [TEXT_BACKGROUND_PRESETS.BLUE]: {
        background: 'var(--ws-text-background-color-blue)',
        color: 'var(--ws-text-highlight-color)',
    },
    [TEXT_BACKGROUND_PRESETS.GREEN]: {
        background: 'var(--ws-text-background-color-green)',
        color: 'var(--ws-text-highlight-color)',
    },
    [TEXT_BACKGROUND_PRESETS.ORANGE]: {
        background: 'var(--ws-text-background-color-orange)',
        color: 'var(--ws-text-highlight-color)',
    },
    [TEXT_BACKGROUND_PRESETS.PINK]: {
        background: 'var(--ws-text-background-color-pink)',
        color: 'var(--ws-text-highlight-color)',
    },
    [TEXT_BACKGROUND_PRESETS.GREY]: {
        background: 'var(--ws-text-background-color-grey)',
        color: 'var(--ws-text-highlight-color)',
    },
};

const FONT_WEIGHT_BOLD = 'var(--font-weight-bold)' as CSSProperties['fontWeight'];

const getColorStyles = (textStyle: string[]): CSSProperties =>
    textStyle.reduce<CSSProperties>((styles, styleName) => {
        const presetStyle = TEXT_STYLE_PRESET_MAP[styleName];
        if (!presetStyle) return styles;

        return { ...styles, ...presetStyle };
    }, {});

const getTextDecoration = (textStyle: string[]): CSSProperties['textDecoration'] | undefined => {
    const textDecorations = [];

    if (textStyle.includes(STYLE_COMMANDS.UNDERLINE)) {
        textDecorations.push('underline');
    }

    if (textStyle.includes(STYLE_COMMANDS.STRIKETHROUGH)) {
        textDecorations.push('line-through');
    }

    return textDecorations.length ? textDecorations.join(' ') : undefined;
};

/**
 * Returns the inline style for rendering plain-text table cells while preserving cell-level text styles.
 */
export const getTableCellPlainTextStyle = (textStyle: string[] = []): CSSProperties => ({
    ...getColorStyles(textStyle),
    fontWeight: textStyle.includes(STYLE_COMMANDS.BOLD) ? FONT_WEIGHT_BOLD : undefined,
    fontStyle: textStyle.includes(STYLE_COMMANDS.ITALIC) ? 'italic' : undefined,
    textDecoration: getTextDecoration(textStyle),
});
